/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import React, { useContext, useEffect, useState, useCallback } from "react";
import { Button, Button2, Footer, Header, Surface, Tab, Text } from "components";
import { BaseWrapper } from "../../styles";
import { ButtonContainer, LoginContainer, TextStyled } from "./styles";
import { Link, useHistory } from "react-router-dom";
import Loader from "components/Loader";
import Colors from "constants/Colors";
import { GlobalAuthContext, GlobalLoaderContext } from "store/context";
import { Field, Form, Formik } from "formik";
import {
  InputField,
  Label,
  MultiSelectField,
  Section,
  SectionHeader,
  SectionLegend,
  SectionTitle,
  SelectField,
} from "components/Form";
import { Col, Row } from "components/Grid";
import * as Yup from "yup";
import { FitinsurAPI, MessageCenter } from "utils";
import Crypt from "utils/src/crypt";
import Access from 'constants/Access';
import SecurityCode from "../security_code";
import TopHeader from "components/TopHeader/index";

export default function LoginPage() {
  console.log('LoginPage - BEGIN');

  const crypt = new Crypt();
  const loading = useContext(GlobalLoaderContext);
  const authContext = useContext(GlobalAuthContext);

  const [loginState, setLoginState] = useState(authContext.status);
  const [subtitleMessage, setSubtitleMessage] = useState(
    'Please enter your account information'
  );

  const [showPassword, setShowPassword] = useState(false);
  const [mfa, setMfa] = useState(false);

  const history = useHistory();

  const goToHomePage = useCallback(() => {
    history.push('home');
  }, [history]);

  const goToChangePassPage = useCallback(() => {
    authContext.changedPassword = false;
    history.push('change-password');
  }, [authContext.changedPassword, history]);

  useEffect(() => localStorage.removeItem('fitinsur-session'), []);

  const handleSubmit = useCallback(
    (values) => {
      loading.start();
      console.log('onSubmit - BEGIN');

      const fitinsur = new FitinsurAPI();
      const mc = new MessageCenter();

      if (!mfa) {
        fitinsur.generateValidationCode(values.username, values.password, Access.APLICATION)
          .then((response) => response.json())
          .then((data) => {
            if (data.ret_code === 200) {
              setSubtitleMessage('Please enter the validation code sent to your e-mail:');
              setMfa(true);
            } else {
              mc.error('Error logging in');
              setSubtitleMessage('Error logging in');
            }

            loading.stop();
          });
      } else {
        const fitinsur = new FitinsurAPI();
        fitinsur.validateVerificationCode(values.username, values.password, values.verificationCode)
          .then((response) => response.json())
          .then((data) => {
            if (data.ret_code === 200) {

              fitinsur
                .requestLogin(values.username, values.password, Access.APLICATION)
                .then((response) => response.json())
                .then((data) => {
                  if (data.ret_code === 200) {

                    const { token, username } = data.ret_value;
                    console.log('requestLogin - Success:', data);

                    authContext.login_response = data;

                    // --------------------------------------------------
                    // production minor fix - change password
                    if (data?.ret_value?.loginCount == 0 || data?.ret_value?.userChangePassword > 0) {
                      loading.stop();
                      goToChangePassPage();
                      return;
                    }
                    // --------------------------------------------------

                    fitinsur
                      .requestAccess(username, "READ", Access.APLICATION, null, null, token)
                      .then((response) => response.json())
                      .then((response) => {
                        if (response.ret_code === 200) {
                          authContext.status = true;
                          authContext.username = values.username;
                          authContext.person_product = data.ret_person_product;
                          authContext.region = data.ret_value.region;
                          authContext.enumProfile = data.ret_value.enumProfile;
                          authContext.firstLogin = false;

                          if (data.ret_token) {
                            authContext.token = data.ret_token;
                            // LEGACY LOGIN
                          }

                          if (data.ret_value.token) {
                            authContext.token = data.ret_value.token;
                            // LOGIN V2
                          }

                          if (data.ret_value) {
                            authContext.firstLogin =
                              Number(data.ret_value.loginCount) <= 0;
                          }

                          localStorage.setItem(
                            'fitinsur-session', JSON.stringify({ "fitinsur-texel": crypt.encryptJson(authContext) })
                          );

                          console.log(authContext);
                          console.log(authContext.firstLogin);
                          console.log('requestLogin: authContext');

                          authContext.firstLogin ||
                            data.ret_value.userChangePassword > 0
                            ? goToChangePassPage()
                            : goToHomePage();
                        } else {
                          authContext.status = false;
                          authContext.username = values.username;
                          authContext.token = '';
                          authContext.person_product = [];
                          authContext.user_region = '';

                          setLoginState(false);

                          mc.error('Error logging in');
                          setSubtitleMessage('Error logging in');
                        }
                      });
                  } else {
                    authContext.status = false;
                    authContext.username = values.username;
                    authContext.token = '';
                    authContext.person_product = [];
                    authContext.user_region = '';

                    setLoginState(false);

                    console.log(data);

                    mc.error('Error logging in');
                    setSubtitleMessage('Error logging in');
                  }

                  loading.stop();
                })
                .catch((error) => {
                  authContext.status = false;
                  authContext.username = values.username;
                  authContext.token = '';
                  authContext.person_product = [];
                  authContext.user_region = '';
                  authContext.login_response = 'ERROR: ' + error.message;

                  loading.stop();
                  setLoginState(false);

                  mc.error('Error logging in');
                  setSubtitleMessage('Error logging in');

                  console.log(error);
                  console.error('requestLogin - Error:', error);
                });
            } else {
              mc.error('Invalid code');
              setSubtitleMessage('Invalid code');
              loading.stop();
            }
          });
      }
      console.log('onSubmit - END');
    },
    [authContext, crypt, goToChangePassPage, goToHomePage, loading]
  );

  return (

    <BaseWrapper>
      <TopHeader />
      <LoginContainer>
        <Header title='Login' subtitle={subtitleMessage} />

        <Formik
          onSubmit={handleSubmit}
          initialValues={{ username: authContext.username || '', password: '' }}
          validationSchema={Yup.object({
            username: Yup.string().required('* Required field'),
            password: Yup.string().required('* Required field'),
          })}
        >
          {(props) => (
            <Form>
              <Surface>
                <Section>
                  {!mfa ? (
                    <Row>
                      <Col xs={12} md={8}>
                        <Field
                          name="username"
                          label="E-mail:"
                          type="text"
                          component={InputField}
                          isRequired
                          hideLabelAsterisk
                          disabled={loginState == true}
                          originalSize
                        />
                      </Col>
                    </Row>
                  ) : ""}

                  {!mfa ? (
                    <Row>
                      <Col xs={12} md={8}>
                        <Field
                          name="password"
                          label="Password:"
                          type={showPassword ? "text" : "password"}
                          component={InputField}
                          isRequired
                          hideLabelAsterisk
                          disabled={loginState == true}
                          originalSize
                          rightIcon={showPassword ? "openEye" : "closedEye"}
                          rightIconClick={() => setShowPassword(state => !state)}
                        />
                      </Col>
                    </Row>
                  ) : ""}

                  {!mfa ? (
                    <Row>
                      <Col xs={12} md={8}>
                        <Link
                          to="forgot-password"
                          style={{ textDecoration: "none" }}
                        >
                          <TextStyled>Forgot your password?</TextStyled>
                        </Link>
                      </Col>
                    </Row>
                  ) : ""}

                  {mfa ? (
                    <Row>
                      <Col xs={12} md={8}>
                        <Field
                          name="verificationCode"
                          label="Security Code:"
                          type="text"
                          component={InputField}
                          isRequired
                          hideLabelAsterisk
                          originalSize
                        />
                      </Col>
                    </Row>
                  ) : ""}

                  <Row>
                    <Col xs={12} md={12} style={{ position: "static" }}>
                      <ButtonContainer>
                        {loginState == true &&
                          ((authContext.status = false), setLoginState(false))}
                        {/* linha de cima é do botão logout na pagina login */}

                        {loginState !== true && (
                          <Button
                            primary
                            type="submit"
                            id="btnLogin"
                            style={{}}
                            disabled={loginState == true}
                          >
                            Next
                          </Button>
                        )}
                      </ButtonContainer>
                    </Col>
                  </Row>
                </Section>
              </Surface>
            </Form>
          )}
        </Formik>
        {loading.active && <Loader backdropColor={Colors.WHITE} fullScreen />}
      </LoginContainer>
      <Footer />
    </BaseWrapper>
  );
}
